import * as React from "react";

import { getCaretRect } from "@/shared/dom";

/**
 * The in-progress trigger token at the caret that a caret autocomplete acts
 * on — a `/slug` in the chat composer, a `[[topic` in the skill-body editor.
 * Both detectors produce this shape, which is what lets them share one
 * autocomplete engine.
 */
export interface CaretToken {
  /**
   * Offset of the token's first character (the `/` or the first `[`).
   * Replaced from here to the caret on accept.
   */
  tokenStart: number;
  /** Text after the trigger up to the caret — the filter query. */
  query: string;
  /**
   * Exclusive end of the token (defaults to the caret). A detector whose
   * grammar autocloses a suffix — e.g. the topic editor's `]]` — reports the
   * offset past that suffix so accept replaces it instead of stranding a
   * duplicate behind the insertion.
   */
  tokenEnd?: number;
}

/** Open-menu state for a caret-anchored autocomplete. */
export interface CaretAutocompleteState {
  /** Offset of the first character of the in-progress token. */
  tokenStart: number;
  /** Caret position when the menu (re)opened. */
  cursor: number;
  /** Post-trigger filter text (may include a `.field` fragment). */
  query: string;
  /** Highlighted suggestion index. */
  highlight: number;
}

interface Params {
  onChange: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  overlayRef: React.RefObject<HTMLElement | null>;
  paperRef: React.RefObject<HTMLElement | null>;
  /** Detect the in-progress token at ``cursor`` (e.g. a `/slug` or `[[topic`). */
  detectToken: (text: string, cursor: number) => CaretToken | null;
  /**
   * Optional selector for elements that should NOT close the menu on mousedown
   * (beyond the textarea and the suggestions paper) — e.g. a portaled MUI
   * ``Select`` listbox nested in a suggestion row.
   */
  keepOpenSelector?: string;
  /**
   * Which glyph the popover anchors to. ``"token-start"`` (default) pins it to
   * the trigger (`/` or `[[`), so it stays put as the query grows — right when
   * the query is short. ``"caret"`` follows the caret, so a menu over a long
   * token (e.g. a `[[/deep/topic/path`) stays under where the author is
   * actually typing instead of drifting left to the token start.
   */
  anchorTo?: "token-start" | "caret";
}

export interface CaretAutocomplete {
  autocomplete: CaretAutocompleteState | null;
  /** Move the highlight to ``index`` (no-op when the menu is closed). */
  setHighlight: (index: number) => void;
  /**
   * Keep the highlight within ``[0, count)`` as the filtered list shrinks. Wire
   * in an effect keyed on the suggestion count; the count lives with the
   * consumer's suggestion computation, so it is passed in rather than owned.
   */
  clampHighlight: (count: number) => void;
  /** Recompute open/closed from the textarea's text + caret. */
  refresh: (text: string, cursor: number) => void;
  /** Close the menu. */
  close: () => void;
  /** Virtual anchor for a caret-following Popper (null when closed). */
  anchorEl: HTMLElement | null;
  /** Keep the overlay's scroll offset in lockstep with the textarea. */
  syncOverlayScroll: () => void;
  /**
   * Handle Arrow/Enter/Tab/Escape while the menu is open, calling ``onAccept``
   * with the highlighted index on Enter/Tab. ``count`` is the current
   * suggestion count (for wrap-around). Returns true if it consumed the event,
   * so a caller with its own Enter behavior (e.g. submit) can bail.
   */
  handleNavKeyDown: (
    event: React.KeyboardEvent,
    count: number,
    onAccept: (index: number) => void,
  ) => boolean;
  /**
   * Replace the active token — recomputed live from the textarea, not from the
   * captured open-time offsets — with ``insertText``, then restore focus.
   *
   * Without ``caretOffset`` the accept is terminal: a trailing space is added
   * when needed and the caret lands after the insertion. With ``caretOffset``
   * the caret is parked *inside* the insertion at that offset (e.g. before an
   * autoclosed `]]`) with no trailing space, and the menu stays suppressed
   * until the text next changes — so the completed token doesn't immediately
   * re-open its own menu.
   */
  accept: (insertText: string, caretOffset?: number) => void;
}

/**
 * Stable ids wiring the ARIA combobox relationship between a caret-autocomplete
 * textarea and its suggestion listbox. The textarea references these via
 * ``aria-controls`` / ``aria-activedescendant``; the consumer that renders the
 * menu marks its listbox and options with the matching ids.
 */
export function useComboboxIds(): {
  listboxId: string;
  optionId: (index: number) => string;
} {
  const base = React.useId();
  return React.useMemo(
    () => ({
      listboxId: `${base}-listbox`,
      optionId: (index: number) => `${base}-opt-${index}`,
    }),
    [base],
  );
}

/**
 * The shared state machine behind a caret-anchored trigger-token autocomplete
 * over a textarea (`/slug`, `[[topic`): open/close tracking, keyboard
 * navigation, highlight clamping,
 * outside-click dismissal, overlay scroll sync, a caret-following Popper anchor,
 * and token-replacing accept. Consumers supply how a token is detected and how
 * many suggestions are shown, and keep their own suggestion computation and
 * menu/overlay rendering. Used by both the chat slug composer and the skill-body
 * topic editor so the mechanics live in one place.
 */
export function useCaretAutocomplete({
  onChange,
  textareaRef,
  overlayRef,
  paperRef,
  detectToken,
  keepOpenSelector,
  anchorTo = "token-start",
}: Params): CaretAutocomplete {
  const [autocomplete, setAutocomplete] =
    React.useState<CaretAutocompleteState | null>(null);

  // Exact text value for which the menu is suppressed — set by a caret-inside
  // accept (the caret is parked within the completed token, which the detector
  // would otherwise immediately re-open). Any change to the text clears it.
  const suppressForValueRef = React.useRef<string | null>(null);

  const close = React.useCallback(() => setAutocomplete(null), []);

  const setHighlight = React.useCallback((index: number) => {
    setAutocomplete((prev) =>
      prev === null ? null : { ...prev, highlight: index },
    );
  }, []);

  const clampHighlight = React.useCallback((count: number) => {
    setAutocomplete((prev) =>
      prev !== null && prev.highlight >= count
        ? { ...prev, highlight: Math.max(0, count - 1) }
        : prev,
    );
  }, []);

  const refresh = React.useCallback(
    (text: string, cursor: number) => {
      if (suppressForValueRef.current !== null) {
        if (suppressForValueRef.current === text) {
          setAutocomplete(null);
          return;
        }
        suppressForValueRef.current = null;
      }
      const active = detectToken(text, cursor);
      if (active === null) {
        setAutocomplete(null);
        return;
      }
      setAutocomplete((prev) => ({
        tokenStart: active.tokenStart,
        cursor,
        query: active.query,
        // Preserve the highlight while typing into the same open token; reset
        // when a different token starts.
        highlight: prev?.tokenStart === active.tokenStart ? prev.highlight : 0,
      }));
    },
    [detectToken],
  );

  // Close on outside click. The textarea and the suggestions paper are carved
  // out so clicking a menu row still accepts before this fires; an optional
  // selector carves out nested portaled popovers (e.g. a Select listbox).
  React.useEffect(() => {
    if (autocomplete === null) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (target === null) {
        return;
      }
      if (paperRef.current?.contains(target)) {
        return;
      }
      if (textareaRef.current?.contains(target)) {
        return;
      }
      if (keepOpenSelector !== undefined && target.closest(keepOpenSelector)) {
        return;
      }
      setAutocomplete(null);
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [autocomplete, paperRef, textareaRef, keepOpenSelector]);

  function syncOverlayScroll() {
    const ta = textareaRef.current;
    const overlay = overlayRef.current;
    if (ta === null || overlay === null) {
      return;
    }
    overlay.scrollTop = ta.scrollTop;
    overlay.scrollLeft = ta.scrollLeft;
  }

  function handleNavKeyDown(
    event: React.KeyboardEvent,
    count: number,
    onAccept: (index: number) => void,
  ): boolean {
    if (autocomplete === null || count === 0) {
      return false;
    }
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setAutocomplete({
        ...autocomplete,
        highlight: (autocomplete.highlight + 1) % count,
      });
      return true;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setAutocomplete({
        ...autocomplete,
        highlight: (autocomplete.highlight - 1 + count) % count,
      });
      return true;
    }
    if (event.key === "Enter" || event.key === "Tab") {
      event.preventDefault();
      onAccept(autocomplete.highlight);
      return true;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      setAutocomplete(null);
      return true;
    }
    return false;
  }

  function accept(insertText: string, caretOffset?: number) {
    const ta = textareaRef.current;
    if (ta === null) {
      return;
    }
    // Recompute the token from the live textarea rather than trusting the
    // captured open-time offsets: the caret may have moved between opening the
    // menu and accepting, and a stale tokenStart would splice at the wrong spot
    // (e.g. doubling the leading trigger).
    const cursor = ta.selectionEnd ?? ta.value.length;
    const active = detectToken(ta.value, cursor);
    if (active === null) {
      setAutocomplete(null);
      return;
    }
    const before = ta.value.slice(0, active.tokenStart);
    // Slice from the token's end (past any autoclosed suffix the detector
    // reports), not the caret, so re-accepting or drilling into an already-
    // closed token reuses its existing suffix instead of doubling it.
    const after = ta.value.slice(active.tokenEnd ?? cursor);
    const needsTrailingSpace = after.length === 0 || !/^\s/.test(after);
    // A caret-inside accept parks the caret within the insertion (e.g. before
    // an autoclosed `]]`), so a trailing space would be stranded mid-token.
    const insertion =
      caretOffset === undefined
        ? insertText + (needsTrailingSpace ? " " : "")
        : insertText;
    const next = before + insertion + after;
    onChange(next);
    setAutocomplete(null);
    if (caretOffset !== undefined) {
      suppressForValueRef.current = next;
    }
    const caret = active.tokenStart + (caretOffset ?? insertion.length);
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }

  const anchorEl = React.useMemo(() => {
    if (autocomplete === null) {
      return null;
    }
    return {
      // Popper calls getBoundingClientRect on its own update cycle; we read the
      // textarea ref + computed caret rect at call time so wrapping text and
      // window resizes are reflected without explicit value-deps churn.
      getBoundingClientRect: () => {
        const ta = textareaRef.current;
        if (ta === null) {
          return new DOMRect(0, 0, 0, 0);
        }
        // Read the caret live so the anchor tracks the current typing position;
        // fall back to the token start otherwise.
        const anchorPos =
          anchorTo === "caret"
            ? (ta.selectionEnd ?? autocomplete.cursor)
            : autocomplete.tokenStart;
        return getCaretRect(ta, anchorPos);
      },
      // Popper expects an HTMLElement shape; nodeType: 1 satisfies the duck-test
      // without returning a real DOM node.
      nodeType: 1,
    } as unknown as HTMLElement;
    // textareaRef is a stable ref object; listed to satisfy exhaustive-deps.
  }, [autocomplete, textareaRef, anchorTo]);

  return {
    autocomplete,
    setHighlight,
    clampHighlight,
    refresh,
    close,
    anchorEl,
    syncOverlayScroll,
    handleNavKeyDown,
    accept,
  };
}
